////////////////////////////////////////////////////////////////////////////////
// Utilities
////////////////////////////////////////////////////////////////////////////////

/**
 * Designed to be extended by other component classes allowing for consistant
 * acccess to methods and utilities
 *
 * @author Mark Notton <mark@marknotton.uk>
 *
 * @link https://github.com/marknotton/helpers-module#helpers.js
 *
 * @license Copyright 2019 Mark Notton
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

 class Utilities {

 	constructor() {

    // If defaults exist, clone it into a settings object
    if ( this.defaults ) {
      this.settings = Object.assign({}, this.defaults);
    } else {
      this.defaults = {}
      this.settings = {}
    }
 	}

  // ===========================================================================
  // Manage Settings
  // ===========================================================================

  /**
    * Manage arguments by their type and trigger a function for each argument type
    *
    * @exmaple
    this.manage(arguments, {
      boolean  : (setting) => { console.log('boolean', setting) },
      function : (setting) => { console.log('function', setting) },
      number   : (setting) => { console.log('number', setting) },
      element  : (setting) => { console.log('element', setting) },
      string   : (setting) => { console.log('string', setting) },
      object   : (setting) => { this.settings = this.merge(this.settings, setting) },
    })
    *
    * @param  {Object} args Directly pass through the functions arguments.
    *
    * @param  {Object} callbacks A collection of methods to call depending on the argument
    *                            type. Dom elements (including jQueryified elements) are
    *                            the only special case that callbacks 'element'.
  */

	manage(args, callbacks) {

    if ( args.length ) {

  		Object.values(args).forEach((setting) => {

        let type = typeof setting

        // jQuery objects should be refered to as an element
        type = window.jQuery && setting instanceof jQuery ? 'element' : type

        // Distinguish if an item is a non jQuery'd Dom element.
        type = setting instanceof Node || setting instanceof Element ? 'element' : type

        // Check if the object is acctualy a simple Array
        type = setting instanceof Array ? 'array' : type

        // If an argument type doesn't match a method type. Throw a warning
        // with instructions on how handle the argument correctly.
        if (!Object.keys(callbacks).includes(type)) {

          console.warn(`${this.constructor.name} class was instantiated with a ${type} argument that hasn't been handled correctly in the manage method. In your ${this.constructor.name} class add the following to the manage function:\n`,
          `this.manage(arguments, { ${type} : (setting) => {...} })`)

        } else {

          callbacks[type](setting)

        }
  		});
    }

	}

  // ==========================================================================
  // Class methods
  // ============================================================================

  /**
    * List out all the methods this class has access to.
    *
    * @method methods
    *
    * @example Get all standard mathods
    * this.methods().forEach(method => { ... }
    *
    * @example Get all static methods
    * this.methods(ClassName).forEach(method => { ... }
    *
    * @return {Array}
    *
    * @todo Exclude any methods starting with an underscore. These should be private.
  */

	methods(classObject) {

    let results = []
    let methods = []

    if (typeof classObject !== 'undefined') {
      methods = Object.getOwnPropertyNames(classObject)
    } else {
      classObject = this;
      methods = Object.getOwnPropertyNames(Object.getPrototypeOf(this))
    }

		for (let name of methods) {
			if (!(classObject[name] instanceof Function) || name == 'constructor' || name == 'init' ) continue;
			results.push(name)
		}

		return results
	}

  // ===========================================================================
  // Extend jQuery
  // ===========================================================================

  /**
    * Merge the contents of this class onto the jQuery prototype to provide
    * new jQuery instance methods.
    *
    * @param  {String} name Class name you'll reference after the jQuery selector
    *
    * @param  {String} method Name of the method you want call
    *
    * @todo Create a varient where Class objects are defined as a getter.
    * So instead of $('body').scroll().foo(...), we can do $('body').scroll.foo(...)
    *
    * @example this.extend('foo', 'init')
    * $('body').foo(...) <- This will pass settings into an 'init' method in the class.
    *
  */

  extend(name = this.constructor.name.toLowerCase(), method) {

    method = typeof method == 'undefined' ? name : method

    let _this = this;

    if ( typeof $.fn[name] !== 'undefined' ) {
      console.log(`You can't extend jQuery's instance method because "${name}" already exists`)
    } else {

      // If a method name exists, reference the current class, don't instantiate
      // a new one for each use of this extend method
      if ( typeof method == 'string' ) {
        $.fn[name] = function(...settings) {
          return _this[method](this, ...settings)
        }
      } else if ( method === false ) {

        // If method is false, create a new class instance and pass the settings
        // into the contructor.
        // $('body').foo(...)
        // is the same as
        // const foo = new Foo(); foo($('body'), ...);
        // $.fn[name] = function(...settings) {
        	// return new (Function.prototype.bind.apply(_this, [null, this].concat(settings)));
        // }
      } else {
        $.fn[name] = function(...settings) {
          return method(...settings, this)
        }
      }

    }
  }

  // ===========================================================================
  // Depp Merge
  // ===========================================================================

  /**
    * A dramatically reduced iteration of TehShrike 'deppmerge' library.
    *
    * @author Josh Duff <https://tehshrike.github.io/>
    *
    * @link https://github.com/TehShrike/deepmerge
    *
    * @param   {Array|Object}  target     If true, do a deep (or recursive) merge [optional]
    *
    * @param   {Array|Object}  source     If true, do a deep (or recursive) merge [optional]
    *
    * @param   {Array}         exceptions By default, if a target item key is different to a setting
    *                                     item key it will be referenced - not cloned. However, if you
    *                                     need to clone it for deepmerging purposes, pass the key
    *                                     into the exceptions array to force a clone.
    *                                     This is to help manage arguments and settings objects
    *                                     that are passed in a different order.
    *
    * @returns {Object}                   Merged values
  */

  merge(target, source, exceptions) {

    const _mergable = value => (!!value && typeof value === 'object')	&& !(Object.prototype.toString.call(value) === '[object RegExp]' || Object.prototype.toString.call(value) === '[object Date]')
    const _clone    = value => (_mergable(value)) ? this.merge((Array.isArray(value) ? [] : {}), value) : value

    // Place exception string into an array
    exceptions = typeof exceptions == 'string' ? [exceptions] : exceptions

  	if (!Array.isArray(source) === Array.isArray(target)) {
  		return _clone(source)
  	} else if (Array.isArray(source)) {
      return target.concat(source).map((element) => _clone(element))
  	} else {
      var destination = {}
      if (_mergable(target)) {
        Object.keys(target).forEach((key) => {
          if (target[key] != this.settings[key] && (typeof exceptions !== 'undefined' && !exceptions.includes(key))) {
            destination[key] = this.settings[key]
          } else {
            destination[key] = _clone(target[key])
          }
        })
      }
      Object.keys(source).forEach((key) => {
        if (!_mergable(source[key]) || !target[key]) {
          destination[key] = _clone(source[key])
        } else {
          destination[key] = this.merge(target[key], source[key])
        }
      })
      return destination
  	}
  }

}
